import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const getProxyUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;
};

const getHeaders = () => {
  const sessionToken = localStorage.getItem('auth_session_token');
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken || '',
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };
};

export interface PackerBasePaint {
  product_naam: string;
  eenheid?: string;
  locatie: string;
  voorraad: number;
  match_type?: string;
  is_out_of_stock?: boolean;
  ignored_brand?: boolean;
  booking_product?: {
    product_id: string;
    product_naam: string | null;
    eenheid: string | null;
  } | null;
}

export interface PackerLineItem {
  lineitemindex: number;
  producttitle: string;
  volume?: string | null;
  color_code?: string | null;
  hex_color?: string | null;
  color_type?: "light" | "dark" | "medium" | null;
  collection_name?: string | null;
  quantity: number;
  image_url?: string | null;
  varianttitle?: string | null;
  base_paint?: PackerBasePaint | null;
}

export interface PackerOrder {
  ordernumber: string;
  orderid: number;
  status: string;
  is_printed: boolean;
  is_future_shipping?: boolean;
  customer: { city?: string };
  item_count: number;
  line_items: PackerLineItem[];
  shippingdate: string | null;
}

export interface PackersListMeta {
  total: number;
  limit: number;
  offset: number;
  packed_today: number;
  recently_cancelled: string[];
  recently_shipped: string[];
}

export interface PackersFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export const usePackersList = (filters: PackersFilters = {}) => {
  const sessionToken = typeof window !== 'undefined' ? localStorage.getItem('auth_session_token') : null;
  return useQuery({
    queryKey: ['packers-list', filters, sessionToken],
    queryFn: async () => {
      const params = new URLSearchParams({ action: 'packers_list' });
      if (filters.search) params.set('search', filters.search);
      params.set('limit', String(filters.limit || 100));
      if (filters.offset) params.set('offset', String(filters.offset));

      const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Fout bij ophalen packers lijst');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json as { success: boolean; data: PackerOrder[]; meta: PackersListMeta };
    },
    enabled: !!sessionToken,
    refetchInterval: 30000,
  });
};

export const useMarkPrinted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_printed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij markeren als afgedrukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) gemarkeerd als afgedrukt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useMarkPacked = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_packed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij inpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) ingepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

export const useUnpack = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'unpack', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij uitpakken');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) uitgepakt`);
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });
};

// Decode base64 PDF and open in new tab
const downloadBase64Pdf = (base64: string, filename: string) => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Fetch a PDF label for a single order and open it in a new tab
export const fetchLabel = async (ordernumber: string): Promise<void> => {
  const params = new URLSearchParams({ action: 'generate_labels', ordernumber });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Fout bij ophalen label voor ${ordernumber}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `labels-${ordernumber}.pdf`);
};

// Fetch labels for multiple orders in a single request
export const fetchLabelsForOrders = async (ordernumbers: string[]): Promise<void> => {
  const params = new URLSearchParams({
    action: 'generate_labels',
    ordernumbers: ordernumbers.join(','),
  });
  const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
  if (!res.ok) throw new Error('Fout bij ophalen labels');
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Onbekende fout');
  if (!json.data?.pdf_base64) throw new Error('Geen PDF ontvangen');
  downloadBase64Pdf(json.data.pdf_base64, `labels-${ordernumbers.join('-')}.pdf`);
};

export const usePrintLabel = () => {
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      await fetchLabelsForOrders(ordernumbers);
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} label(s) gedownload`);
    },
    onError: (error: Error) => {
      if (error.message.includes('base paint') || error.message.includes('No items')) {
        toast.error('Labels niet beschikbaar: basisverf-matching ontbreekt voor deze order(s). Dit moet eerst in het kleurenorders-systeem worden verwerkt.', { duration: 6000 });
      } else {
        toast.error(error.message);
      }
    },
  });
};
